import * as React from "react"
import { toast as sonnerToast, Toaster as SonnerToaster } from "sonner"

/*
   Exists for UNDO. Only "undo" surfaces are worth a toast — successful
   saves are shown by the interface changing, not by a card sliding in.

   Duration policy: errors persist (do not auto-dismiss); action toasts
   get 8s; plain toasts get 4s.
*/

export type ToastTone = "neutral" | "success" | "error"

export interface ToastMessage {
  id: string
  tone?: ToastTone
  title: React.ReactNode
  body?: React.ReactNode
  /** Label and handler. Presence extends the duration. */
  action?: { label: string; onAction: () => void }
  /** Override the default. Errors ignore this and stay. */
  durationMs?: number
}

interface ToastContextValue {
  toast: (t: Omit<ToastMessage, "id">) => string
  dismiss: (id: string) => void
}

const ToastContext = React.createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>")
  return ctx
}

/**
 * Mount once near the root. Provides `useToast()` and renders the sonner
 * viewport (singleton — do not mount more than one).
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const toast = React.useCallback((t: Omit<ToastMessage, "id">) => {
    const tone: ToastTone = t.tone ?? "neutral"
    const duration =
      tone === "error"
        ? Number.POSITIVE_INFINITY
        : (t.durationMs ?? (t.action ? 8000 : 4000))

    const options: Parameters<typeof sonnerToast>[1] = {}
    if (t.body !== undefined) options.description = t.body
    if (Number.isFinite(duration)) options.duration = duration
    if (t.action) {
      options.action = { label: t.action.label, onClick: t.action.onAction }
    }

    const id =
      tone === "success"
        ? sonnerToast.success(t.title, options)
        : tone === "error"
          ? sonnerToast.error(t.title, options)
          : sonnerToast(t.title, options)

    return String(id)
  }, [])

  const dismiss = React.useCallback((id: string) => {
    sonnerToast.dismiss(id)
  }, [])

  const value = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <SonnerToaster
        position="bottom-right"
        closeButton
        richColors
        toastOptions={{
          classNames: {
            toast:
              "flex items-start gap-2.5 rounded-lg border border-border bg-surface p-3.5 shadow-e3",
            title: "text-body-sm font-medium text-text",
            description: "mt-0.5 text-caption leading-normal text-text-2",
            actionButton:
              "shrink-0 rounded-md border border-border px-2.5 py-1 text-caption font-medium text-text-2 hover:border-border-strong hover:text-text",
          },
        }}
      />
    </ToastContext.Provider>
  )
}

